import { KgInsight } from '@api/api/kg-insights';
import { Button } from '@design/components/button/Button';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useTrackEmailDrawerOpen } from '@terminal/hooks/use-track-email-drawer-open/useTrackEmailDrawerOpen';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useTranslation } from 'react-i18next';
import {
  getCatalystEmailDrawerTracking,
  getCatalystTrackingKey,
} from '../../utils/catalyst-widget-utils';
import { KgInsightPreview } from '../kg-insight-content/KgInsightPreview';
import { TopTakeaways } from '../top-takeaways/TopTakeaways';

type CatalystWidgetInsightContentProps = {
  kgInsight: KgInsight;
  navigateToKGInsight: () => void;
};

export const CatalystWidgetInsightContent = ({
  kgInsight,
  navigateToKGInsight,
}: CatalystWidgetInsightContentProps) => {
  const { t } = useTranslation([
    'insights',
    'earnings',
    'whatsNew',
    'stream',
    'widget',
    'countries',
  ]);
  const { params } = useQueryParams();
  const { trackCatalystDrawerOpen } = usePosthogEvents();

  const entityTag = params[queryKeys.entityTag] ?? undefined;
  const themeId = params[queryKeys.themeId] ?? undefined;
  const utmSource = params[queryKeys.utmSource] ?? undefined;
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  useTrackEmailDrawerOpen({
    isDrawerOpen: true,
    utmSource,
    trackingKey: getCatalystTrackingKey({
      insightId: kgInsight.id,
      category: kgInsight.category,
      entityTag,
      themeId,
    }),
    onTrackEmailOpen: () => {
      const { type, entities, themeIds } = getCatalystEmailDrawerTracking({
        category: kgInsight.category,
        insightId: kgInsight.id,
        entityTag,
        themeId,
      });

      trackCatalystDrawerOpen({
        source: POSTHOG_TRACKING_SOURCES.EMAIL,
        type,
        insightId: kgInsight.id,
        entities,
        themeIds,
      });
    },
  });

  return (
    <KgInsightPreview
      kgInsight={kgInsight}
      hideCatalystBadge={isExpanded}
      keyStatsItemsPerRow={3}
    >
      <TopTakeaways
        kgInsight={kgInsight}
        source={
          kgInsight.category === 'company_catalyst'
            ? POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER
            : POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER
        }
      />
      <Button
        onClick={navigateToKGInsight}
        fullWidth
        label={t('insights:catalystGeneration.readFullCatalyst')}
        data-testid="read-full-catalyst"
      />
    </KgInsightPreview>
  );
};
